import { calculateRequiredScope, scopeMatches, withPublicPostReadScopes, withPublicGroupReadScopes, withPublicProfileReadScopes, } from './scopeHelpers.js';
import { viewerBlockedTarget, targetBlockedViewer } from './profileHelpers.js';
export const checkCapabilities = (neededCapabilities, presentCapabilities = {
    add: [],
    remove: [],
}) => {
    const containsCapability = (c, clist) => !!clist?.find((pc) => pc === c || (pc.includes('*') && c.startsWith(pc.split('*')[0])));
    const missingCapabilities = neededCapabilities.filter((c) => containsCapability(c, presentCapabilities.remove ?? []) ||
        !containsCapability(c, presentCapabilities.add ?? []));
    return {
        success: missingCapabilities.length === 0,
        missingCapabilities,
    };
};
export const mergeCapabilities = (capabilities) => {
    return {
        add: [...new Set(capabilities?.map((c) => c?.add || []).flat() ?? [])],
        remove: [
            ...new Set(capabilities?.map((c) => c?.remove || []).flat() ?? []),
        ],
    };
};
/** Order-independent equality check for two capability add/remove lists. */
export const capabilitySetsEqual = (a, b) => {
    const sa = new Set(a ?? []);
    const sb = new Set(b ?? []);
    if (sa.size !== sb.size)
        return false;
    return [...sa].every((value) => sb.has(value));
};
export const checkRoleCapabilities = (roles = [], neededCapabilities) => checkCapabilities(neededCapabilities, mergeCapabilities(roles.map((r) => r.capabilities)));
/** Any of these matching `roles` shows the Administration row (submenu is separate UX). */
export const ADMIN_SIDEBAR_MENU_CAPABILITIES = [
    'admin',
    'core-roles-read',
    'core-logs-read',
    'core-accounts-list',
    'core-backups-read',
    'core-analytics-read',
    'core-inviteLinks-read',
    'core-maintenance-restart',
    'core-config-read',
    'core-customization-read',
];
export const hasAdminSidebarAccess = (roles = []) => ADMIN_SIDEBAR_MENU_CAPABILITIES.some((cap) => checkRoleCapabilities(roles, [cap]).success);
export const isLocal = (profile) => checkRoleCapabilities(profile.roles, ['core-local']).success;
/**
 * Returns `['local', 'none']` when the caller is "trusted local" — either a local
 * profile or an authenticated service — and `['none']` otherwise.
 */
export const localOrNone = (authData) => {
    if (authData.service) {
        return ['local', 'none'];
    }
    if (authData.profile && isLocal(authData.profile)) {
        return ['local', 'none'];
    }
    return ['none'];
};
export const getGroupRelationships = (authData, group) => {
    const membershipRoles = authData.profile?.memberships.find((m) => m.group.id === group.id)?.roles ??
        [];
    if (authData.profile || authData.service) {
        return [...localOrNone(authData), ...membershipRoles];
    }
    return ['none'];
};
export const getProfileRelationships = (authData, otherProfile) => {
    const { profile } = authData;
    if (profile && profile.id === otherProfile?.id) {
        return ['self', ...localOrNone(authData)];
    }
    const relationships = localOrNone(authData);
    if (profile?.followers.map((f) => f.id).includes(otherProfile?.id)) {
        relationships.push('followed-by');
    }
    if (profile?.following.map((f) => f.id).includes(otherProfile?.id)) {
        relationships.push('following');
    }
    if (viewerBlockedTarget(profile, otherProfile?.id)) {
        relationships.push('blocked');
    }
    if (targetBlockedViewer(profile, otherProfile?.id)) {
        relationships.push('blocked-by');
    }
    return relationships;
};
export const getPostRelationships = (authData, post) => {
    const { profile } = authData;
    const relationships = [];
    if (['public', 'local'].includes(post.visibility)) {
        relationships.push(...localOrNone(authData));
    }
    if (profile && post.audience?.some((a) => a.id === profile.id)) {
        relationships.push('audience');
    }
    if (profile && post.mentions?.some((m) => m.profile.id === profile.id)) {
        relationships.push('mentioned');
    }
    if (profile &&
        post.entries?.some((e) => e.type === 'rsvp' &&
            e.profile.id === profile.id &&
            ['yes', 'tentative'].includes(e.data.response))) {
        relationships.push('attendee');
    }
    return relationships;
};
export const getReportRelationships = (authData, report) => {
    const { profile } = authData;
    if (profile && report.reporterProfile.id === profile.id) {
        return [...localOrNone(authData), 'reporter'];
    }
    if (profile && report.reportedProfile.id === profile.id) {
        return [...localOrNone(authData), 'reported'];
    }
    return localOrNone(authData);
};
export const getAccessTokenRelationships = (authData, accessToken) => {
    const { profile } = authData;
    const relationships = [...localOrNone(authData)];
    const ownerProfileId = accessToken.ownedBy?.id;
    if (ownerProfileId && profile?.id === ownerProfileId) {
        relationships.push('owner');
    }
    return relationships;
};
const getConfigCapabilities = (object, relationships, config) => mergeCapabilities(relationships.map((r) => {
    const group = config[object];
    return group[r] ?? { add: [], remove: [] };
}));
const getGroupRelationshipCapabilities = (authData, group) => mergeCapabilities(group
    ? getGroupRelationships(authData, group).map((r) => group.capabilities?.[r] ?? { add: [], remove: [] })
    : []);
export const getGroupCapabilitiesByRoles = (roles, group) => mergeCapabilities((roles ?? []).map((r) => group?.capabilities?.[r] ?? { add: [], remove: [] }));
// Per-group powers (add member, post in group, moderate, …) come from the
// caller's relationship to the group (edge roles plus none/local), never from
// their instance role. Instance roles only gate group *creation*
// (`core-groups-create`), checked separately at the creation entry points.
export const getGroupCapabilities = (authData, group) => getGroupRelationshipCapabilities(authData, group);
const getRoleCapabilities = (profile) => mergeCapabilities(profile?.roles.map((r) => r.capabilities) ?? []);
export const getProfileCapabilities = (authData, targetProfile, config) => getConfigCapabilities('profile', getProfileRelationships(authData, targetProfile), config);
const getPostVisibilityCapabilities = (post, profile) => {
    switch (post.visibility) {
        case 'public':
            return { add: ['core-posts-read'], remove: [] };
        case 'direct':
            return {};
        // Group posts derive their capabilities from the group relationship
        // (`getGroupCapabilities`) and the author's `self` relationship only —
        // never from instance roles. This keeps per-group powers consistent with
        // every other group check (see getGroupCapabilities); instance roles only
        // gate group creation.
        case 'group':
            return {};
        default:
            return getRoleCapabilities(profile);
    }
};
const getScopedPostReadCapabilities = (authData, neededCapabilities, post) => {
    if (authData.profile ||
        post.visibility !== 'local' ||
        neededCapabilities.length !== 1 ||
        neededCapabilities[0] !== 'core-posts-read' ||
        !scopeMatches({
            scopes: authData.scopes,
            requiredScope: {
                scopeLevel: 'admin',
                resource: { type: 'posts', id: post.id },
            },
        })) {
        return { add: [], remove: [] };
    }
    return { add: ['core-posts-read'], remove: [] };
};
export const getPostCapabilities = (authData, post, config) => mergeCapabilities([
    getPostVisibilityCapabilities(post, authData.profile),
    getConfigCapabilities('post', getPostRelationships(authData, post), config),
    getConfigCapabilities('profile', getProfileRelationships(authData, post.profile), config),
    getGroupCapabilities(authData, post.group),
]);
const getReportCapabilities = (authData, report, config) => mergeCapabilities([
    getConfigCapabilities('report', getReportRelationships(authData, report), config),
    getRoleCapabilities(authData.profile),
]);
const getAccessTokenCapabilities = (authData, accessToken, config) => mergeCapabilities([
    getConfigCapabilities('accessToken', getAccessTokenRelationships(authData, accessToken), config),
    getRoleCapabilities(authData.profile),
]);
const getAccountCapabilities = (authData, targetAccount) => mergeCapabilities([
    authData.account?.id === targetAccount.id
        ? { add: ['core-accounts-update'], remove: [] }
        : { add: [], remove: [] },
    getRoleCapabilities(authData.profile),
]);
const checkCapabilitiesWithCalculatedScope = (capabilities, authData, neededCapabilities, resource) => {
    const requiredScope = calculateRequiredScope({
        resource,
        requiredCapabilities: neededCapabilities,
    });
    return checkCapabilitiesWithExplicitScope(capabilities, authData, neededCapabilities, requiredScope);
};
const checkCapabilitiesWithExplicitScope = (capabilities, authData, neededCapabilities, requiredScope) => {
    const capabilitiesResult = checkCapabilities(neededCapabilities, capabilities);
    const scopeResult = scopeMatches({ scopes: authData.scopes, requiredScope });
    return {
        success: capabilitiesResult.success && scopeResult,
        missingCapabilities: capabilitiesResult.missingCapabilities,
        missingScope: scopeResult ? undefined : requiredScope,
    };
};
export const checkGroupCapabilities = (authData, neededCapabilities, group) => checkCapabilitiesWithCalculatedScope(getGroupCapabilities(authData, group), withPublicGroupReadScopes(authData, group, neededCapabilities), neededCapabilities, { type: 'groups', id: group.id });
export const checkPostCapabilities = (authData, neededCapabilities, post, config) => {
    const capabilities = mergeCapabilities([
        getPostCapabilities(authData, post, config),
        getScopedPostReadCapabilities(authData, neededCapabilities, post),
    ]);
    return checkCapabilitiesWithCalculatedScope(capabilities, withPublicPostReadScopes(authData, post, neededCapabilities), neededCapabilities, { type: 'posts', id: post.id });
};
export const checkProfileCapabilities = (authData, neededCapabilities, targetProfile, config) => checkCapabilitiesWithCalculatedScope(getProfileCapabilities(authData, targetProfile, config), withPublicProfileReadScopes(authData, targetProfile, neededCapabilities, config), neededCapabilities, { type: 'profiles', id: targetProfile.id });
export const checkReportCapabilities = (authData, neededCapabilities, report, config) => checkCapabilitiesWithCalculatedScope(getReportCapabilities(authData, report, config), authData, neededCapabilities, { type: 'reports', id: report.id });
export const checkAccessTokenCapabilities = (authData, neededCapabilities, accessToken, config) => checkCapabilitiesWithExplicitScope(getAccessTokenCapabilities(authData, accessToken, config), authData, neededCapabilities, { scopeLevel: 'admin', resource: { type: 'self' } });
export const checkAccountCapabilities = (authData, neededCapabilities, targetAccount) => checkCapabilitiesWithExplicitScope(getAccountCapabilities(authData, targetAccount), authData, neededCapabilities, { scopeLevel: 'admin', resource: { type: 'self' } });
/** Capability required to create accounts when public sign-ups are closed. */
export const ACCOUNT_CREATE_CAPABILITY = 'core-accounts-create';
/**
 * Whether the caller may register a new account/profile while sign-ups are closed.
 * Profile tokens need the role capability; service tokens are treated as having it
 * and are gated by write scope on profiles (same as other create operations).
 */
export const checkAccountCreateAuthorization = (authData) => {
    const neededCapabilities = [ACCOUNT_CREATE_CAPABILITY];
    const capabilities = authData.service
        ? { add: neededCapabilities, remove: [] }
        : getRoleCapabilities(authData.profile);
    return checkCapabilitiesWithCalculatedScope(capabilities, authData, neededCapabilities, { type: 'profiles', id: '*' });
};
